// 바닥 드롭 월드 표식(D2a, PRD 4.2 "바닥 빛기둥"): 등급 색 빛기둥 + 떠 있는 보석. 강화석은 낮은 청회색 결정.
// 드롭 식별 회차(2026-10-27): 장비는 등급색 바닥 고리 + 빛기둥(영웅 이상·전설은 흰 심지, 전설은 솟는 반짝이까지), 강화석·보석은 작고 낮은 빛줄기 + 작은 고리라
// 멀리서도 '장비(높고 굵은 기둥)'와 '재료(낮고 가는 빛)'가 갈린다. 이름·그림 이름표는 render/lootLabels.ts.
// GameView.loot 고정 풀을 그대로 인덱스로 그린다(인스턴싱 6개, 프레임마다 새 객체 없음). UI 팝업은 쓰지 않는다 — 피드백은 월드 오브젝트와
// 화면 가장자리 띠(ui/lootFeed.ts)뿐. 전설·세트만 기둥이 높고 굵다(희소성 신호). 줍는 순간 연출(기둥이 접히며 영웅에게 빨려 드는 나선 줄기)은
// vfx/gearFx.ts가 Ev.LootPickup으로 그린다(c017). 기둥 높이·폭 표는 그쪽과 같은 값을 쓴다.
// 이름 있는 유니크(l1029, item.nu): 표 칸 5 — 영웅 등급이지만 주황(#ff8c2e) 기둥이 전설보다 높고 굵고, 흰 심지·솟는 주황 반짝이 4개,
// 바닥 고리 두 겹(안쪽 고리 + 천천히 숨 쉬는 바깥 고리). 떨어지는 순간의 내리꽂힘은 gearFx.namedDrop.
import * as THREE from 'three/webgpu';
import { Scene, combatScene, type GameView } from '../shared/types';
import { GRADE_BEAM, LOOT_ARM_TICKS, NAMED_BEAM, NAMED_FX_INDEX } from '../shared/content/loot';
import { LOOT_BEAM_H, LOOT_BEAM_W, STONE_COLOR } from '../vfx/gearFx';
import { GEM_COLORS } from '../shared/content/consumables';

/** 장비 기둥 속 흰 심지 색(영웅 이상) */
const CORE_COLOR = 0xffffff;
/** 전설 솟는 반짝이 개수(드롭 하나당). 이름 있는 유니크는 하나 더 */
const SPARKS_PER_LEGEND = 3;
const SPARKS_PER_NAMED = 4;
/** 강화석·보석의 낮은 빛줄기: 높이(m)·폭 배율 */
const SMALL_BEAM_H = 0.95, SMALL_BEAM_W = 0.4;

const GRADE_ORDER = ['common', 'rare', 'epic', 'set', 'legend'] as const;
/** 등급별 기둥 높이(m)·폭 배율(줍기 연출의 접히는 기둥과 같은 표) */
const BEAM_H = LOOT_BEAM_H;
const BEAM_W = LOOT_BEAM_W;

export class LootFx {
  readonly group = new THREE.Group();
  private readonly beams: THREE.InstancedMesh;
  private readonly gems: THREE.InstancedMesh;
  private readonly stones: THREE.InstancedMesh;
  /** 바닥 고리(장비 등급색·재료 작은 고리) */
  private readonly rings: THREE.InstancedMesh;
  /** 전설 기둥을 따라 솟는 반짝이 */
  private readonly sparks: THREE.InstancedMesh;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly p = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly colors: THREE.Color[] = [...GRADE_ORDER.map((g) => new THREE.Color(GRADE_BEAM[g])), new THREE.Color(NAMED_BEAM)];
  private readonly stoneColor = new THREE.Color(STONE_COLOR);
  private readonly coreColor = new THREE.Color(CORE_COLOR);
  /** 이름 있는 유니크 심지: 흰색이면 밝은 바닥 위 가산 혼합에서 기둥 전체가 연노랑으로 떠서(실입력 캡처 l1029) 주황 심지를 쓴다 */
  private readonly namedCoreColor = new THREE.Color(0xff7a1a);
  private readonly all: THREE.InstancedMesh[];
  /** 보석 색(D2c, GEM_COLORS 순서) */
  private readonly gemColors: THREE.Color[] = GEM_COLORS.map((c) => new THREE.Color(c.color));

  constructor(capacity: number) {
    // 위로 갈수록 투명해지는 기둥(정점 색 대신 원통 위쪽을 좁혀 가볍게). 가산 혼합·깊이 쓰기 없음
    const beamGeo = new THREE.CylinderGeometry(0.06, 0.16, 1, 10, 1, true);
    beamGeo.translate(0, 0.5, 0);
    const beamMat = new THREE.MeshBasicNodeMaterial({ transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false });
    // 기둥 몸통 + (영웅 이상) 흰 심지 / 강화석·보석 낮은 빛줄기가 같은 메시를 쓴다: 드롭당 최대 2개
    this.beams = new THREE.InstancedMesh(beamGeo, beamMat, capacity * 2);
    const gemMat = new THREE.MeshBasicNodeMaterial({ transparent: true, opacity: 0.95, depthWrite: true, fog: false });
    this.gems = new THREE.InstancedMesh(new THREE.OctahedronGeometry(0.16, 0), gemMat, capacity);
    // 강화석·보석(D2c)이 같은 작은 결정 메시를 쓴다: 색은 인스턴스 색(강화석 청회·보석 색)이라 재질 색은 흰색
    const stoneMat = new THREE.MeshBasicNodeMaterial({ color: 0xffffff, transparent: true, opacity: 0.95, fog: false });
    this.stones = new THREE.InstancedMesh(new THREE.OctahedronGeometry(0.11, 0), stoneMat, capacity);
    const ringGeo = new THREE.RingGeometry(0.36, 0.5, 28);
    ringGeo.rotateX(-Math.PI / 2);
    const ringMat = new THREE.MeshBasicNodeMaterial({ transparent: true, opacity: 0.75, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false });
    // 이름 있는 유니크는 고리 두 겹이라 드롭당 최대 2칸
    this.rings = new THREE.InstancedMesh(ringGeo, ringMat, capacity * 2);
    const sparkMat = new THREE.MeshBasicNodeMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
    this.sparks = new THREE.InstancedMesh(new THREE.OctahedronGeometry(0.07, 0), sparkMat, capacity * SPARKS_PER_NAMED);
    const all = this.all = [this.beams, this.gems, this.stones, this.rings, this.sparks];
    for (const im of all) {
      im.frustumCulled = false; im.count = 0; im.renderOrder = 4;
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.group.add(im);
    }
    // 인스턴스 색 버퍼를 미리 만든다(첫 setColorAt이 버퍼를 만들며 할당하지 않게)
    for (let i = 0; i < capacity; i++) { this.gems.setColorAt(i, this.colors[0]); this.stones.setColorAt(i, this.stoneColor); }
    for (let i = 0; i < capacity * 2; i++) this.rings.setColorAt(i, this.colors[0]);
    for (let i = 0; i < capacity * 2; i++) this.beams.setColorAt(i, this.colors[0]);
    for (let i = 0; i < capacity * SPARKS_PER_NAMED; i++) this.sparks.setColorAt(i, this.colors[4]);
    for (const im of all) im.instanceColor!.setUsage(THREE.DynamicDrawUsage);
  }

  /** nowMs: 벽시계(맥동·회전). 던전이 아니면 숨긴다 */
  update(view: GameView, nowMs: number): void {
    const l = view.loot;
    if (!combatScene(view.flow.scene)) { this.group.visible = false; return; }
    this.group.visible = true;
    const t = nowMs / 1000;
    let nb = 0, ng = 0, ns = 0, nr = 0, nsp = 0;
    for (let i = 0; i < l.capacity; i++) {
      if (!l.active[i]) continue;
      const x = l.x[i], z = l.z[i];
      // 떨어지는 순간 튀어 오르는 포물선(대기 틱 동안), 그 뒤 위아래 흔들림
      const k = Math.min(1, l.age[i] / LOOT_ARM_TICKS);
      const pop = (1 - k) * Math.sin(k * Math.PI) * 0.9;
      const bob = 0.35 + Math.sin(t * 2.4 + i) * 0.06 + pop;
      if (l.kind[i] === 1 || l.kind[i] === 2) {
        // 강화석(청회) · 보석(D2c: 색 번호 = grade 칸, 조금 크고 높이 떠서 돈다)
        const gem = l.kind[i] === 2;
        const col = gem ? this.gemColors[Math.min(this.gemColors.length - 1, l.grade[i])] : this.stoneColor;
        this.q.setFromAxisAngle(this.up, t * (gem ? 2.2 : 1.6) + i);
        this.m.compose(this.p.set(x, (gem ? 0.32 : 0.22) + pop, z), this.q, gem ? this.s.set(1.35, 1.7, 1.35) : this.s.set(1, 1.3, 1));
        this.stones.setColorAt(ns, col);
        this.stones.setMatrixAt(ns++, this.m);
        // 재료는 작고 낮은 빛줄기 + 작은 고리(장비의 높고 굵은 기둥과 한눈에 갈린다)
        this.q.identity();
        this.m.compose(this.p.set(x, 0, z), this.q, this.s.set(SMALL_BEAM_W, SMALL_BEAM_H * (0.4 + 0.6 * k), SMALL_BEAM_W));
        this.beams.setMatrixAt(nb, this.m); this.beams.setColorAt(nb, col); nb++;
        this.m.compose(this.p.set(x, 0.03, z), this.q, this.s.set(0.55, 1, 0.55));
        this.rings.setMatrixAt(nr, this.m); this.rings.setColorAt(nr, col); nr++;
        continue;
      }
      const named = !!l.item[i]?.nu;
      const g = named ? NAMED_FX_INDEX : Math.min(4, l.grade[i]);
      const c = this.colors[g];
      // 기둥: 등급이 높을수록 높고 굵다. 맥동은 폭만(섬광 없음)
      const pulse = 1 + Math.sin(t * 3 + i * 1.7) * 0.08;
      this.q.identity();
      const bh = BEAM_H[g] * (0.4 + 0.6 * k);
      this.m.compose(this.p.set(x, 0, z), this.q, this.s.set(BEAM_W[g] * pulse, bh, BEAM_W[g] * pulse));
      this.beams.setMatrixAt(nb, this.m); this.beams.setColorAt(nb, c); nb++;
      // 영웅 이상: 기둥 속 흰 심지(가늘고 같은 높이) — 일반·희귀 기둥보다 한눈에 밝다
      if (g >= 2) {
        this.m.compose(this.p.set(x, 0, z), this.q, this.s.set(BEAM_W[g] * 0.4 * pulse, bh, BEAM_W[g] * 0.4 * pulse));
        this.beams.setMatrixAt(nb, this.m); this.beams.setColorAt(nb, named ? this.namedCoreColor : this.coreColor); nb++;
      }
      // 바닥 고리: 등급색, 등급이 높을수록 큼
      this.m.compose(this.p.set(x, 0.03, z), this.q, this.s.set(0.8 + 0.18 * g, 1, 0.8 + 0.18 * g));
      this.rings.setMatrixAt(nr, this.m); this.rings.setColorAt(nr, c); nr++;
      // 이름 있는 유니크: 천천히 숨 쉬는 바깥 고리(크기만 변한다 — 밝기 깜빡임 없음)
      if (named) {
        const br = 2.05 + Math.sin(t * 1.6 + i) * 0.18;
        this.m.compose(this.p.set(x, 0.025, z), this.q, this.s.set(br * k, 1, br * k));
        this.rings.setMatrixAt(nr, this.m); this.rings.setColorAt(nr, c); nr++;
      }
      // 전설·이름 있는 유니크: 기둥을 따라 솟는 반짝이(느린 상승, 섬광 아님)
      if (g >= 4 && k >= 1) {
        const n = named ? SPARKS_PER_NAMED : SPARKS_PER_LEGEND;
        for (let j = 0; j < n; j++) {
          const ph = (t * 0.55 + j / n + i * 0.13) % 1;
          const ang = t * 2.6 + j * 2.1 + i;
          this.q.setFromAxisAngle(this.up, ang * 2);
          this.m.compose(this.p.set(x + Math.cos(ang) * 0.32, 0.2 + ph * (BEAM_H[g] - 0.4), z + Math.sin(ang) * 0.32), this.q, this.s.setScalar(1.4 * (1 - ph * 0.6)));
          this.sparks.setColorAt(nsp, c);
          this.sparks.setMatrixAt(nsp++, this.m);
        }
        this.q.identity();
      }
      this.q.setFromAxisAngle(this.up, t * 2 + i);
      this.m.compose(this.p.set(x, bob, z), this.q, this.s.set(1, 1.4, 1));
      this.gems.setMatrixAt(ng, this.m); this.gems.setColorAt(ng, c); ng++;
    }
    this.beams.count = nb; this.gems.count = ng; this.stones.count = ns; this.rings.count = nr; this.sparks.count = nsp;
    for (const im of this.all) { im.instanceMatrix.needsUpdate = true; im.instanceColor!.needsUpdate = true; }
  }

  dispose(): void {
    for (const im of this.all) { im.geometry.dispose(); (im.material as THREE.Material).dispose(); im.dispose(); }
  }
}
